import React from 'react';
import { Flex, styled } from '@withjoy/joykit';

import { CustomComponentEmptyState } from './components';
import { useCustomData } from './hooks/useCustomData';
import { sortBySortOrder } from '@shared/utils/array';
import { filterInvalidComponents } from '@website-editor/customComponents/CustomComponents';
import {
  GuestMediaGalleryComponentFragment,
  GuestPhotoComponentFragment,
  GuestTextComponentFragment,
  GuestTravelMapComponentFragment,
  GuestVideoComponentFragment,
  JsonDataBlobComponentFragment,
} from '@graphql/generated';
import { useTranslation } from '@shared/core';
import { useIsFeatureEnabled } from '@shared/core/featureFlags';
import { pxToRem } from '@withjoy/joykit/theme';
import { GuestSiteCustomJsonComponentWrapper } from './components/GuestSiteCustomJsonComponentWrapper';
import { createLazyComponentFromNamedExport } from '@shared/utils/createLazyComponent';

const RichText = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/guestSiteRichText" */ './components/RichText'), 'RichText');

const Accommodations = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/guestSiteAccommodations" */ './components/Accommodations'), 'Accommodations');

const Photo = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/guestSitePhoto" */ './components/Photo'), 'Photo');

const Video = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/guestSiteVideo" */ './components/Video'), 'Video');

const PhotoGallery = createLazyComponentFromNamedExport(() => import(/* webpackChunkName: "content/guestSitePhotoGallery" */ './components/PhotoGallery'), 'PhotoGallery');
interface Props {
  pageId: string;
  eventId: string;
  pageSlug: string;
  eventHandle: string;
  pageTitle?: string;
  subTitle?: string | null;
  isBrannanLayout?: boolean;
}

const ComponentsContainer = styled(Flex)`
  flex-wrap: wrap;
  gap: ${pxToRem(48)};
  align-items: stretch;
  width: 100%;
  justify-content: flex-start;
`;

export const Custom: React.FC<Props> = props => {
  const { pageSlug, eventId, eventHandle, isBrannanLayout } = props;
  const { loading, pageContainerBySlug } = useCustomData(eventId, eventHandle, pageSlug);

  const { t } = useTranslation('guestSiteCustom');
  const emptyStateTransFn = t('emptyState');

  const [isPhotoGalleryCustomBlockEnabled] = useIsFeatureEnabled('photoGalleryCustomBlockEnabled');

  const hasCustomComponents = pageContainerBySlug?.components ? pageContainerBySlug.components.length > 0 : false;

  const sortedComponents = sortBySortOrder(pageContainerBySlug?.components?.filter(filterInvalidComponents) ?? []);
  const firstOfTypeTextIndex = sortedComponents.findIndex(component => component.__typename === 'TextComponent');

  const customComponents = sortedComponents
    .map((componentData, index) => {
      switch (componentData.__typename) {
        case 'TravelMapComponent':
          return (
            <Accommodations
              key={componentData.id}
              {...props}
              travelMapComponentData={componentData as GuestTravelMapComponentFragment}
            />
          );
        case 'TextComponent':
          return (
            <RichText
              key={componentData.id}
              isBrannanLayout={isBrannanLayout}
              isFirstOfType={index === firstOfTypeTextIndex}
              richTextComponentData={componentData as GuestTextComponentFragment}
            />
          );
        case 'PhotoComponent':
          return (
            <Photo
              key={componentData.id}
              photoComponentData={componentData as GuestPhotoComponentFragment}
            />
          );
        case 'VideoComponent':
          return (
            <Video
              key={componentData.id}
              videoComponentData={componentData as GuestVideoComponentFragment}
            />
          );
        case 'MediaGalleryComponent':
          return isPhotoGalleryCustomBlockEnabled ? (
            <PhotoGallery
              key={componentData.id}
              mediaGalleryComponentData={componentData as GuestMediaGalleryComponentFragment}
            />
          ) : null;
        case 'JsonDataBlobComponent':
          return (
            <GuestSiteCustomJsonComponentWrapper
              key={componentData.id}
              jsonComponentData={componentData as JsonDataBlobComponentFragment}
            />
          );
        default:
          return null;
      }
    })
    .filter((element): element is JSX.Element => element !== null);

  if (loading) {
    return <></>;
  }
  return (
    <Flex
      flexDirection={'column'}
      width={'100%'}
      rowGap={pxToRem(45)}
      alignItems="center"
    >
      {hasCustomComponents ? (
        <ComponentsContainer flexDirection={['column', 'row']}>
          {customComponents.map((component, index) => (
            <React.Fragment key={`custom-components-guest-${index}`}>{component}</React.Fragment>
          ))}
        </ComponentsContainer>
      ) : (
        <CustomComponentEmptyState
          title={emptyStateTransFn.title()}
          subTitle={emptyStateTransFn.content()}
          isBrannanLayout={props.isBrannanLayout}
        />
      )}
    </Flex>
  );
};
